import { useEffect, useRef, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/gallery");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...items];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data: media } = await api.post("/api/media/upload", fd);
        const { data: item } = await api.post("/api/admin/gallery", {
          image_url: media.url,
          caption: "",
          category: "general",
          sort_order: next.length,
        });
        next.unshift(item);
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    setItems(next);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    toast.success("Uploaded");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image from gallery?")) return;
    try {
      await api.delete(`/api/admin/gallery/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/api/admin/gallery/${editItem.id}`, {
        image_url: editItem.image_url,
        caption: editItem.caption,
        category: editItem.category,
        sort_order: editItem.sort_order || 0,
      });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      setEditItem(null);
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gallery-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload images that appear on the public gallery.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-primary hover:bg-primary-dark text-white" data-testid="gallery-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload images
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No images yet. Upload to get started.</Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <Card key={it.id} className="overflow-hidden group relative" data-testid={`gallery-item-${it.id}`}>
              <img src={mediaUrl(it.image_url)} alt={it.caption} className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-2 opacity-0 group-hover:opacity-100">
                <div className="flex gap-2 w-full">
                  <Button size="sm" variant="secondary" className="flex-1" onClick={() => setEditItem(it)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="destructive" className="flex-1" onClick={() => remove(it.id)} data-testid={`delete-gallery-${it.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {it.caption && (
                <div className="p-2 text-xs text-muted-foreground truncate">{it.caption}</div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit gallery item</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <img src={mediaUrl(editItem.image_url)} alt="" className="w-full max-h-60 object-contain bg-muted rounded-md" />
              <div>
                <Label>Caption</Label>
                <Input value={editItem.caption || ""} onChange={(e) => setEditItem({ ...editItem, caption: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={editItem.category || ""} onChange={(e) => setEditItem({ ...editItem, category: e.target.value })} />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={editItem.sort_order || 0} onChange={(e) => setEditItem({ ...editItem, sort_order: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-primary hover:bg-primary-dark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
